import React, { useState, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Rectangle, Marker, Tooltip, useMapEvents } from 'react-leaflet';
import { LatLngBoundsExpression, LeafletMouseEvent } from 'leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Geofence } from '../services/GeofenceService';

import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const defaultMarkerIcon = new Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export interface GeofenceMapProps {
  geofences: Geofence[];
  selectedGeofenceId?: string | null;
  onSelectGeofence: (id: string) => void;
  createMode: boolean;
  onRectangleCreated: (ne: { lat: number; lng: number }, sw: { lat: number; lng: number }) => void;
  onCancelCreate?: () => void;
}

const DEFAULT_CENTER: [number, number] = [40.0, -74.0];
const DEFAULT_ZOOM = 10;

const GEOFENCE_COLOR = '#28a745';
const SELECTED_COLOR = '#007bff';
const PREVIEW_COLOR = '#fd7e14';

function computeBounds(
  p1: { lat: number; lng: number },
  p2: { lat: number; lng: number }
): { ne: { lat: number; lng: number }; sw: { lat: number; lng: number } } {
  const ne = {
    lat: Math.max(p1.lat, p2.lat),
    lng: Math.max(p1.lng, p2.lng),
  };
  const sw = {
    lat: Math.min(p1.lat, p2.lat),
    lng: Math.min(p1.lng, p2.lng),
  };
  return { ne, sw };
}

interface MapClickHandlerProps {
  createMode: boolean;
  firstPoint: { lat: number; lng: number } | null;
  onFirstClick: (point: { lat: number; lng: number }) => void;
  onSecondClick: (point: { lat: number; lng: number }) => void;
}

const MapClickHandler: React.FC<MapClickHandlerProps> = ({
  createMode,
  firstPoint,
  onFirstClick,
  onSecondClick,
}) => {
  useMapEvents({
    click(e: LeafletMouseEvent) {
      if (!createMode) return;
      const { lat, lng } = e.latlng;
      if (!firstPoint) {
        onFirstClick({ lat, lng });
      } else {
        onSecondClick({ lat, lng });
      }
    },
  });
  return null;
};

const GeofenceMap: React.FC<GeofenceMapProps> = ({
  geofences,
  selectedGeofenceId,
  onSelectGeofence,
  createMode,
  onRectangleCreated,
  onCancelCreate,
}) => {
  const [firstPoint, setFirstPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [previewRect, setPreviewRect] = useState<{
    ne: { lat: number; lng: number };
    sw: { lat: number; lng: number };
  } | null>(null);

  // Reset state when create mode is turned off
  useEffect(() => {
    if (!createMode) {
      setFirstPoint(null);
      setPreviewRect(null);
    }
  }, [createMode]);

  const handleFirstClick = useCallback((point: { lat: number; lng: number }) => {
    setFirstPoint(point);
  }, []);

  const handleSecondClick = useCallback(
    (point: { lat: number; lng: number }) => {
      if (!firstPoint) return;
      const { ne, sw } = computeBounds(firstPoint, point);
      setPreviewRect({ ne, sw });
      onRectangleCreated(ne, sw);
      // Reset for next creation
      setFirstPoint(null);
      setPreviewRect(null);
    },
    [firstPoint, onRectangleCreated]
  );

  const handleCancelCreate = useCallback(() => {
    setFirstPoint(null);
    setPreviewRect(null);
    onCancelCreate?.();
  }, [onCancelCreate]);

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      {createMode && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            background: 'rgba(255,255,255,0.95)',
            padding: '8px 16px',
            borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontWeight: 500 }}>
            {firstPoint
              ? 'Click the second corner to complete the rectangle'
              : 'Click the map to place the first corner'}
          </span>
          <button
            onClick={handleCancelCreate}
            style={{
              background: '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Cancel
          </button>
        </div>
      )}

      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <MapClickHandler
          createMode={createMode}
          firstPoint={firstPoint}
          onFirstClick={handleFirstClick}
          onSecondClick={handleSecondClick}
        />

        {/* Render geofence rectangles */}
        {geofences.map((geofence) => {
          const isSelected = geofence.id === selectedGeofenceId;
          const bounds: LatLngBoundsExpression = [
            [geofence.sw.lat, geofence.sw.lng],
            [geofence.ne.lat, geofence.ne.lng],
          ];
          return (
            <Rectangle
              key={geofence.id}
              bounds={bounds}
              pathOptions={{
                color: isSelected ? SELECTED_COLOR : GEOFENCE_COLOR,
                weight: isSelected ? 3 : 2,
                fillOpacity: isSelected ? 0.25 : 0.15,
              }}
              eventHandlers={{
                click: () => onSelectGeofence(geofence.id),
              }}
            >
              <Tooltip sticky>{geofence.name}</Tooltip>
            </Rectangle>
          );
        })}

        {/* First corner marker in create mode */}
        {createMode && firstPoint && (
          <Marker
            position={[firstPoint.lat, firstPoint.lng]}
            icon={defaultMarkerIcon}
          />
        )}

        {/* Preview rectangle */}
        {previewRect && (
          <Rectangle
            bounds={[
              [previewRect.sw.lat, previewRect.sw.lng],
              [previewRect.ne.lat, previewRect.ne.lng],
            ]}
            pathOptions={{
              color: PREVIEW_COLOR,
              weight: 2,
              dashArray: '6 4',
              fillOpacity: 0.1,
            }}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default GeofenceMap;
